import {
  LivretEleveCompetence,
  LivretEleveMatiere,
  LsuError,
  LSUErrorCode,
  TypePositionnement,
} from '@teetsh/app/src/containers/Livrets/types/livret';
import _flatMap from 'lodash-es/flatMap';
import { ErrorLabelAction } from 'containers/Livrets/useLSUErrorsMapping';

function hasAtLeastOneCompetenceWithAPositionnement(
  competences: LivretEleveCompetence[],
): boolean {
  return competences.some(c => c.note !== undefined && c.note >= 0);
}

export function checkLsuForMatieres(
  matieres: LivretEleveMatiere[],
  typePositionnement: TypePositionnement,
  errorsMapping: (
    error: LSUErrorCode,
    params?: {
      [key: string]: string;
    },
  ) => ErrorLabelAction | undefined,
): LsuError[] {
  let errors: LsuError[] = [];
  const allCompetences = matieres.reduce((acc, num) => {
    const domainesCompetences = _flatMap(
      num.domaines,
      d => d.competences || [],
    );
    return [...acc, ...(num.competences || []), ...domainesCompetences];
  }, []);
  if (allCompetences.length === 0) {
    errors = [
      ...errors,
      {
        code: 'no_competences',
        message: errorsMapping('no_competences')?.label || '',
      },
    ];
  }
  const matieresAndDomainesErrors = matieres.reduce((acc, num) => {
    if (
      num.note !== undefined &&
      num.note >= 0 &&
      (num.competences || []).length === 0 &&
      (num.domaines || []).length === 0
    ) {
      const error = errorsMapping('domain_has_no_competences_but_a_note', {
        name: num.name,
      });
      return [
        ...acc,
        {
          code: 'domain_has_no_competences_but_a_note',
          message: error?.label || '',
        } as LsuError,
      ];
    }
    return [
      ...acc,
      ...(num.domaines || []).reduce((accc, dom) => {
        if (
          dom.note !== undefined &&
          dom.note >= 0 &&
          (dom.competences || []).length === 0
        ) {
          const error = errorsMapping('domain_has_no_competences_but_a_note', {
            name: num.name,
          });
          return [
            ...accc,
            {
              code: 'domain_has_no_competences_but_a_note',
              message: error?.label || '',
            } as LsuError,
          ];
        }
        return accc;
      }, [] as LsuError[]),
    ];
  }, [] as LsuError[]);
  errors = [...errors, ...matieresAndDomainesErrors];
  const compTooLongErrors = allCompetences.reduce((acc, num) => {
    if ((num.name || []).length === 0) {
      const error = errorsMapping('empty_competence_name');
      return [
        ...acc,
        {
          code: 'empty_competence_name',
          message: error?.label || '',
        } as LsuError,
      ];
    }
    if (num.name.length > 300) {
      const error = errorsMapping('too_long_competence_name', {
        name: `${num.name.slice(0, 50)}...`,
      });
      return [
        ...acc,
        {
          code: 'too_long_competence_name',
          message: error?.label || '',
        } as LsuError,
      ];
    }
    return acc;
  }, errors);
  errors = compTooLongErrors;

  const nbOfCompletedDomaines = matieres.reduce((acc, mat) => {
    if (typePositionnement === 'competence') {
      if (hasAtLeastOneCompetenceWithAPositionnement(mat.competences || [])) {
        return acc + 1;
      }

      const completedDomaines = (mat.domaines || []).filter(
        d =>
          (d.competences || []).length > 0 &&
          hasAtLeastOneCompetenceWithAPositionnement(d.competences),
      );
      return completedDomaines.length > 0 ? acc + 1 : acc;
    }
    if (
      (mat.competences || []).length > 0 &&
      mat.note !== undefined &&
      mat.note >= 0 &&
      (mat.domaines || []).length === 0
    ) {
      return acc + 1;
    }
    const completedDomaines = (mat.domaines || []).filter(
      d =>
        (d.competences || []).length > 0 && d.note !== undefined && d.note >= 0,
    );

    return completedDomaines.length > 0 ? acc + 1 : acc;
  }, 0);
  if (nbOfCompletedDomaines < 3) {
    const error = errorsMapping('should_have_3_completed_domains');
    errors = [
      ...errors,
      {
        code: 'should_have_3_completed_domains',
        message: error?.label || '',
      },
    ];
  }
  return errors;
}
